"use client";

import { c as _c } from "react/compiler-runtime";
import { formatQuantityWithUnitKey } from "@/lib/formatting";
import { useState } from "react";
import { CheckCircle, XCircle, AlertCircle, FileText, MapPin, Hash, Loader2, ExternalLink, Sparkles, RotateCcw } from "lucide-react";
import { formatDate } from "@/lib/formatting";
import { useDealApiMutation } from "@/lib/deals/useDealApiMutation";
import type { DealVerification, VerificationStatus } from "@/lib/deals/verification";
import { dealDocumentUrl } from "@/lib/deals/document-download/refs";
interface AdminVerificationPanelProps {
  dealId: string;
  verification: DealVerification | null;
  offeringLocation?: string;
  /** What the deal's row offers, in its unit (`offeringUnit`), to compare with the Ökokonto. */
  offeringQuantity?: number;
  offeringUnit?: string | null;
  offeringDocumentUrl?: string | null;
  offeringDocumentName?: string | null;
  onStatusChange?: (verification: DealVerification) => void;
  className?: string;
}
type OcrLocationDetails = {
  source?: string;
  ocrKreis?: string | null;
  ocrBundesland?: string | null;
  ocrGemarkungen?: string[];
  offeringLandkreis?: string | null;
  offeringBundesland?: string | null;
  confidence?: number;
};
type OcrQuantityDetails = {
  source?: string;
  ocrTotalGuthaben?: number | null;
  offeringQuantity?: number | null;
  confidence?: number;
};

/**
 * AdminVerificationPanel - Admin panel for verifying Ökokonto documents
 */
export function AdminVerificationPanel(t0) {
  const $ = _c(71);
  const {
    dealId,
    verification,
    offeringLocation,
    offeringQuantity,
    offeringUnit,
    offeringDocumentUrl,
    offeringDocumentName,
    onStatusChange,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [submittingAction, setSubmittingAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  let t2;
  if ($[0] !== onStatusChange) {
    t2 = data => {
      onStatusChange?.(data);
      setSubmittingAction(null);
      if (data.verification_status === "verified") {
        setSuccessMessage("\xD6kokonto erfolgreich verifiziert.");
      } else {
        if (data.verification_status === "needs_info") {
          setSuccessMessage("Info-Anfrage an den Verk\xE4ufer gesendet.");
        } else {
          if (data.verification_status === "failed") {
            setSuccessMessage("\xD6kokonto wurde abgelehnt.");
          } else {
            if (data.verification_status === "pending") {
              setSuccessMessage(null);
            }
          }
        }
      }
    };
    $[0] = onStatusChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setSubmittingAction(null);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = {
      onSuccess: t2,
      onError: t3
    };
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    mutate,
    isSubmitting,
    error
  } = useDealApiMutation(t4);
  const locationOcr = verification?.location_match_details as OcrLocationDetails | null;
  const quantityOcr = verification?.quantity_match_details as OcrQuantityDetails | null;
  const hasOcrData = locationOcr?.source === "ocr" || quantityOcr?.source === "ocr";
  const [locationMatch, setLocationMatch] = useState(verification?.location_match ?? undefined);
  const [quantityMatch, setQuantityMatch] = useState(verification?.quantity_match ?? undefined);
  const [feedback, setFeedback] = useState(verification?.admin_feedback || "");
  const documentUrl = verification?.okokonto_document_url || offeringDocumentUrl;
  const documentName = verification?.okokonto_document_name || offeringDocumentName;
  const hasDocument = !!documentUrl;
  const isFromOffering = !verification?.okokonto_document_url && !!offeringDocumentUrl;
  let t5;
  if ($[5] !== dealId || $[6] !== offeringDocumentUrl || $[7] !== verification?.okokonto_document_url) {
    t5 = verification?.okokonto_document_url ? dealDocumentUrl(dealId, "verification-okokonto") : offeringDocumentUrl ? dealDocumentUrl(dealId, "offering-okokonto") : null;
    $[5] = dealId;
    $[6] = offeringDocumentUrl;
    $[7] = verification?.okokonto_document_url;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const documentHref = t5;
  const isPending = verification?.verification_status === "pending" || verification?.verification_status === "needs_info" || !verification && hasDocument;
  let t6;
  if ($[9] !== dealId || $[10] !== feedback || $[11] !== locationMatch || $[12] !== mutate || $[13] !== quantityMatch) {
    t6 = status => {
      setSubmittingAction(status);
      setSuccessMessage(null);
      mutate(`/api/admin/deals/${dealId}/verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          verification_status: status,
          location_match: locationMatch,
          quantity_match: quantityMatch,
          admin_feedback: feedback || undefined
        })
      });
    };
    $[9] = dealId;
    $[10] = feedback;
    $[11] = locationMatch;
    $[12] = mutate;
    $[13] = quantityMatch;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleVerify = t6;
  if (!hasDocument) {
    const t7 = `rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${className}`;
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="flex items-center gap-3 text-zinc-500"><AlertCircle className="w-5 h-5" /><p className="text-sm">Noch keine Dokumente hochgeladen</p></div>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t7) {
      t9 = <div className={t7}>{t8}</div>;
      $[16] = t7;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  const t7 = `rounded-lg border border-zinc-200 bg-white ${className}`;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h3 className="font-medium text-zinc-900">Verifizierung</h3>;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== hasOcrData) {
    t9 = hasOcrData && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700"><Sparkles className="w-3 h-3" />Automatisch erkannt</span>;
    $[19] = hasOcrData;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t9) {
    t10 = <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50"><div className="flex items-center justify-between">{t8}{t9}</div></div>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <FileText className="w-5 h-5 text-zinc-500" />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = documentName || "\xD6kokonto-Dokument";
  let t13;
  if ($[24] !== t12) {
    t13 = <p className="text-sm font-medium text-zinc-700">{t12}</p>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== isFromOffering || $[27] !== verification?.uploaded_at) {
    t14 = isFromOffering ? "Aus Angebotserstellung" : `Hochgeladen: ${formatDate(verification?.uploaded_at)}`;
    $[26] = isFromOffering;
    $[27] = verification?.uploaded_at;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14) {
    t15 = <p className="text-xs text-zinc-500">{t14}</p>;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t13 || $[32] !== t15) {
    t16 = <div className="flex items-center gap-3">{t11}<div>{t13}{t15}</div></div>;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== documentHref || $[35] !== documentUrl) {
    t17 = documentUrl && <a href={documentHref ?? undefined} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">Öffnen<ExternalLink className="w-3.5 h-3.5" /></a>;
    $[34] = documentHref;
    $[35] = documentUrl;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">{t16}{t17}</div>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== error || $[41] !== feedback || $[42] !== handleVerify || $[43] !== isPending || $[44] !== isSubmitting || $[45] !== locationMatch || $[46] !== locationOcr || $[47] !== offeringLocation || $[48] !== offeringQuantity || $[49] !== offeringUnit || $[50] !== quantityMatch || $[51] !== quantityOcr || $[52] !== submittingAction || $[53] !== successMessage) {
    t19 = isPending && <><div className="space-y-3"><div><div className="flex items-center justify-between"><div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-400" /><span className="text-sm text-zinc-700">Standort stimmt überein</span>{offeringLocation && <span className="text-xs text-zinc-400">({offeringLocation})</span>}</div><div className="flex gap-2"><button type="button" onClick={() => setLocationMatch(true)} className={`p-1.5 rounded ${locationMatch === true ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400 hover:text-zinc-600"}`}><CheckCircle className="w-4 h-4" /></button><button type="button" onClick={() => setLocationMatch(false)} className={`p-1.5 rounded ${locationMatch === false ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-400 hover:text-zinc-600"}`}><XCircle className="w-4 h-4" /></button></div></div>{locationOcr?.source === "ocr" && <div className="ml-6 mt-1 text-xs text-violet-600 bg-violet-50 rounded px-2 py-1">OCR:{" "}{[locationOcr.ocrKreis, locationOcr.ocrBundesland].filter(Boolean).join(", ") || "nicht erkannt"}{locationOcr.ocrGemarkungen && locationOcr.ocrGemarkungen.length > 0 && <span className="text-violet-500">{" "}(Gemarkungen: {locationOcr.ocrGemarkungen.join(", ")})</span>}</div>}</div><div><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Hash className="w-4 h-4 text-zinc-400" /><span className="text-sm text-zinc-700">Menge stimmt überein</span>{offeringQuantity && offeringUnit && <span className="text-xs text-zinc-400">({formatQuantityWithUnitKey(offeringQuantity, offeringUnit)})</span>}</div><div className="flex gap-2"><button type="button" onClick={() => setQuantityMatch(true)} className={`p-1.5 rounded ${quantityMatch === true ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400 hover:text-zinc-600"}`}><CheckCircle className="w-4 h-4" /></button><button type="button" onClick={() => setQuantityMatch(false)} className={`p-1.5 rounded ${quantityMatch === false ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-400 hover:text-zinc-600"}`}><XCircle className="w-4 h-4" /></button></div></div>{quantityOcr?.source === "ocr" && <div className="ml-6 mt-1 text-xs text-violet-600 bg-violet-50 rounded px-2 py-1">OCR:{" "}{quantityOcr.ocrTotalGuthaben != null ? formatQuantityWithUnitKey(quantityOcr.ocrTotalGuthaben, "oekopunkte") : "nicht erkannt"}</div>}</div></div><div><label className="block text-sm font-medium text-zinc-700 mb-1">Feedback (optional)</label><textarea value={feedback} onChange={e => setFeedback(e.target.value)} placeholder={"Hinweise f\xFCr den Verk\xE4ufer..."} rows={2} className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" /></div>{error && <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle className="w-4 h-4" /><span>{error}</span></div>}{successMessage && <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3"><CheckCircle className="w-4 h-4 flex-shrink-0" /><span>{successMessage}</span></div>}<div className="flex gap-2 pt-2"><button type="button" onClick={() => handleVerify("verified")} disabled={isSubmitting || locationMatch !== true || quantityMatch !== true} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">{isSubmitting && submittingAction === "verified" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}Verifizieren</button><button type="button" onClick={() => handleVerify("needs_info")} disabled={isSubmitting} className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">{isSubmitting && submittingAction === "needs_info" && <Loader2 className="w-4 h-4 animate-spin" />}Info anfordern</button><button type="button" onClick={() => handleVerify("failed")} disabled={isSubmitting} className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50">{isSubmitting && submittingAction === "failed" && <Loader2 className="w-4 h-4 animate-spin" />}Ablehnen</button></div></>;
    $[40] = error;
    $[41] = feedback;
    $[42] = handleVerify;
    $[43] = isPending;
    $[44] = isSubmitting;
    $[45] = locationMatch;
    $[46] = locationOcr;
    $[47] = offeringLocation;
    $[48] = offeringQuantity;
    $[49] = offeringUnit;
    $[50] = quantityMatch;
    $[51] = quantityOcr;
    $[52] = submittingAction;
    $[53] = successMessage;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== handleVerify || $[56] !== isPending || $[57] !== isSubmitting || $[58] !== locationOcr || $[59] !== quantityOcr || $[60] !== submittingAction || $[61] !== verification) {
    t20 = !isPending && verification && <div className="space-y-2"><div className="flex items-center gap-2"><span className="text-sm text-zinc-500">Status:</span><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${verification.verification_status === "verified" ? "bg-emerald-100 text-emerald-700" : verification.verification_status === "failed" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{verification.verification_status === "verified" && "Verifiziert"}{verification.verification_status === "failed" && "Abgelehnt"}{verification.verification_status === "needs_info" && "Info angefordert"}</span></div>{verification.location_match !== null && <p className="text-sm text-zinc-600">Standort:{" "}{verification.location_match ? "\u2713 Stimmt \xFCberein" : "\u2717 Stimmt nicht \xFCberein"}{locationOcr?.source === "ocr" && <span className="text-xs text-violet-500 ml-1">(OCR:{" "}{[locationOcr.ocrKreis, locationOcr.ocrBundesland].filter(Boolean).join(", ") || "\u2014"})</span>}</p>}{verification.quantity_match !== null && <p className="text-sm text-zinc-600">Menge: {verification.quantity_match ? "\u2713 Stimmt \xFCberein" : "\u2717 Stimmt nicht \xFCberein"}{quantityOcr?.source === "ocr" && <span className="text-xs text-violet-500 ml-1">(OCR:{" "}{quantityOcr.ocrTotalGuthaben != null ? formatQuantityWithUnitKey(quantityOcr.ocrTotalGuthaben, "oekopunkte") : "\u2014"})</span>}</p>}{verification.admin_feedback && <p className="text-sm text-zinc-600">Feedback: {verification.admin_feedback}</p>}<button type="button" onClick={() => handleVerify("pending")} disabled={isSubmitting} className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-800 border border-zinc-300 hover:border-zinc-400 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">{isSubmitting && submittingAction === "pending" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}Zurücksetzen</button></div>;
    $[55] = handleVerify;
    $[56] = isPending;
    $[57] = isSubmitting;
    $[58] = locationOcr;
    $[59] = quantityOcr;
    $[60] = submittingAction;
    $[61] = verification;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== t18 || $[64] !== t19 || $[65] !== t20) {
    t21 = <div className="p-4 space-y-4">{t18}{t19}{t20}</div>;
    $[63] = t18;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== t10 || $[68] !== t21 || $[69] !== t7) {
    t22 = <div className={t7}>{t10}{t21}</div>;
    $[67] = t10;
    $[68] = t21;
    $[69] = t7;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  return t22;
}